import { useState } from 'react';
import { Paperclip, Download, Loader2, AlertCircle } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import type { MessageAttachment } from '@/types/messaging';

interface Props {
  attachments: MessageAttachment[];
  /** Message bubble background is `bg-primary`/`bg-muted` (see Messages.tsx) — controls
   * whether this renders with light-on-dark or dark-on-light muted text/border tones. */
  isConsultant: boolean;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Presentational attachment list for a message bubble in the consultant app (issue #559
 * follow-up — attachments were fully wired up in apps/client's messaging but never surfaced
 * here). Renders nothing when `attachments` is empty.
 *
 * Unlike apps/client's MessageAttachmentList, this calls MessagingService.getAttachmentDownloadUrl
 * (an edge-function action) rather than Supabase Storage directly — src/ has no direct-to-Storage
 * frontend calls anywhere (Service → API Client → Edge Function is mandatory here), and the
 * backend generates the signed URL using its own authenticated client via `createSignedUrl`.
 */
export default function MessageAttachmentList({ attachments, isConsultant }: Props) {
  const t = useTranslations('consultantMessages.attachment');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [failedId, setFailedId] = useState<string | null>(null);

  // Defensive: `attachments` should always be an array per ConsultantMessage's type, but a
  // response path that omits it (e.g. a locally-optimistic message before the server round
  // trip) must never crash the whole thread view over a field that's legitimately empty.
  if (!attachments || attachments.length === 0) return null;

  const handleDownload = async (attachment: MessageAttachment) => {
    setDownloadingId(attachment.id);
    setFailedId(null);
    try {
      const url = await ServiceRegistry.getMessagingService().getAttachmentDownloadUrl(attachment.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      // Surfaced, not swallowed — a failed download must be visible, not a silent no-op click.
      setFailedId(attachment.id);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div
      className={`mt-2 space-y-1 border-t pt-2 ${
        isConsultant ? 'border-primary-foreground/20' : 'border-foreground/10'
      }`}
    >
      {attachments.map((attachment) => (
        <div key={attachment.id}>
          <button
            type="button"
            onClick={() => handleDownload(attachment)}
            disabled={downloadingId === attachment.id}
            className="flex w-full items-center gap-1.5 rounded text-xs opacity-90 hover:opacity-100 hover:underline text-left"
          >
            {downloadingId === attachment.id ? (
              <Loader2 className="h-3 w-3 shrink-0 animate-spin" />
            ) : (
              <Paperclip className="h-3 w-3 shrink-0" />
            )}
            <span className="truncate">{attachment.filename}</span>
            <span className="shrink-0 opacity-70">({formatSize(attachment.size_bytes)})</span>
            <Download className="h-3 w-3 shrink-0 ml-auto" />
          </button>
          {failedId === attachment.id && (
            <p
              className={`flex items-center gap-1 text-xs mt-0.5 ${
                isConsultant ? 'text-primary-foreground' : 'text-destructive'
              }`}
            >
              <AlertCircle className="h-3 w-3 shrink-0" />
              {t('downloadFailed')}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
